/**
 * Feedback Reward Service v1.0
 * 
 * Rewards users with token refunds (up to 15%) for providing feedback on AI responses.
 * Includes anti-abuse protections and AI training data collection.
 */

import { Message, User } from '../types';

// ==================== Types ====================

export interface FeedbackRecord {
    messageId: string;
    sessionId: string;
    userId: string;
    feedbackType: 'up' | 'down';
    timestamp: number;
    rewarded: boolean;
    rewardAmount: number;
    messageContent: string;
    responseContent: string;
    tokensCharged: number;
    complexity: string;
    model: string;
    // AI Training data
    trainingData?: {
        userQuery: string;
        aiResponse: string;
        feedbackScore: number; // 1 for up, -1 for down
        responseQuality: 'good' | 'bad';
        timestamp: number;
        metadata: Record<string, any>;
    };
}

export interface FeedbackRewardResult {
    success: boolean;
    rewarded: boolean;
    refundAmount: number;
    reason: string;
    isFirstFeedback: boolean;
    abuseDetected: boolean;
    feedbackId: string;
}

export interface AbuseDetectionResult {
    isAbuse: boolean;
    reason: string;
    severity: 'low' | 'medium' | 'high';
    action: 'allow' | 'warn' | 'block';
}

export interface FeedbackStats {
    totalFeedbacks: number;
    thumbsUp: number;
    thumbsDown: number;
    totalRefunded: number;
    uniqueMessagesRated: number;
    lastFeedbackTime: number;
}

// ==================== Configuration ====================

const FEEDBACK_CONFIG = {
    // SCALED Reward settings based on response length
    // Refund percentage now scales inversely with response length
    // Shorter responses = higher refund % (since less value delivered)
    // Longer responses = lower refund % (since more value was delivered)
    
    // Response length tiers (in characters)
    responseLengthTiers: {
        tiny: 500,       // < 500 chars
        short: 1500,     // 500-1500 chars
        medium: 4000,    // 1500-4000 chars
        long: 10000,     // 4000-10000 chars
        veryLong: 25000, // 10000-25000 chars
        massive: Infinity // 25000+ chars
    },
    
    // Refund percentages by length tier (inverse scaling) - INCREASED for better rewards
    refundByLengthTier: {
        tiny: 25,        // 25% refund for very short responses
        short: 20,       // 20% for short
        medium: 15,      // 15% for medium
        long: 12,        // 12% for long
        veryLong: 10,    // 10% for very long
        massive: 8       // 8% for massive responses
    },
    
    // Bonus modifiers (additive, capped at limits) - INCREASED for better rewards
    bonusForThumbsDown: 5,          // +5% for thumbs down (valuable for improvement)
    bonusForComplex: 3,             // +3% for complex queries
    maxRefundPercent: 30,           // Absolute maximum refund (INCREASED from 25%)
    minRefundPercent: 5,            // Absolute minimum refund (INCREASED from 2%)
    
    // Anti-abuse settings
    maxFeedbacksPerHour: 30,        // Max feedbacks per hour
    maxFeedbacksPerDay: 100,        // Max feedbacks per day
    minTimeBetweenFeedbacks: 2000,  // Min 2 seconds between feedbacks
    suspiciousPatternThreshold: 10, // Consecutive same feedback type
    
    // Cooldown periods (ms)
    hourlyResetInterval: 3600000,   // 1 hour
    dailyResetInterval: 86400000,   // 24 hours
    
    // Storage keys
    storageKey: 'feedback_reward_data',
    statsKey: 'feedback_stats',
    trainingDataKey: 'ai_training_feedback'
};

// ==================== Feedback Reward Service ====================

class FeedbackRewardService {
    private rewardedMessages: Set<string> = new Set();
    private feedbackHistory: FeedbackRecord[] = [];
    private hourlyFeedbackCount: number = 0;
    private dailyFeedbackCount: number = 0;
    private lastFeedbackTime: number = 0;
    private consecutiveSameFeedback: number = 0;
    private lastFeedbackType: 'up' | 'down' | null = null;
    private hourlyResetTime: number = Date.now();
    private dailyResetTime: number = Date.now();
    
    constructor() {
        this.loadState();
        this.startPeriodicReset();
    }
    
    /**
     * Process feedback and calculate reward
     */
    async processFeedback(
        message: Message,
        previousUserMessage: Message | null,
        feedbackType: 'up' | 'down',
        user: User | null,
        sessionId: string,
        updateUser?: (updates: Partial<User>) => void
    ): Promise<FeedbackRewardResult> {
        const messageId = message.id;
        const feedbackId = `fb_${messageId}_${Date.now()}`;
        
        console.log('[FeedbackReward] Processing feedback:', {
            messageId,
            feedbackType,
            tokensCharged: message.tokensCharged,
            contentLength: message.content?.length || 0
        });
        
        // Check if already rewarded for this message
        if (this.rewardedMessages.has(messageId)) {
            console.log('[FeedbackReward] Already rewarded for this message');
            return {
                success: true,
                rewarded: false,
                refundAmount: 0,
                reason: 'Already provided feedback for this message',
                isFirstFeedback: false,
                abuseDetected: false,
                feedbackId
            };
        }
        
        // Check for abuse
        const abuseCheck = this.detectAbuse(feedbackType);
        if (abuseCheck.action === 'block') {
            console.log('[FeedbackReward] Abuse detected:', abuseCheck.reason);
            return {
                success: false,
                rewarded: false,
                refundAmount: 0,
                reason: abuseCheck.reason,
                isFirstFeedback: true,
                abuseDetected: true,
                feedbackId
            };
        }
        
        // Calculate refund amount
        // If tokensCharged is not set, estimate from content length
        let tokensCharged = message.tokensCharged || 0;
        if (tokensCharged <= 0 && message.content) {
            // Estimate tokens from content: roughly 1 token per 4 characters
            tokensCharged = Math.round(message.content.length / 4);
            console.log('[FeedbackReward] Estimated tokens from content:', tokensCharged);
        }
        
        const refundPercent = this.calculateRefundPercent(message, feedbackType, previousUserMessage);
        const refundAmount = Math.round(tokensCharged * (refundPercent / 100));
        
        console.log('[FeedbackReward] Refund calculation:', {
            tokensCharged,
            refundPercent,
            refundAmount
        });
        
        // Only reward if there's something to refund
        if (refundAmount <= 0 || tokensCharged <= 0) {
            // Still mark as rewarded to prevent re-attempts
            this.rewardedMessages.add(messageId);
            this.saveState();
            
            console.log('[FeedbackReward] No tokens to refund');
            return {
                success: true,
                rewarded: false,
                refundAmount: 0,
                reason: 'No tokens to refund for this message',
                isFirstFeedback: true,
                abuseDetected: false,
                feedbackId
            };
        }
        
        // Apply refund to user
        if (user && updateUser) {
            const newUsage = Math.max(0, user.dailyTokenUsage - refundAmount);
            updateUser({ dailyTokenUsage: newUsage });
            console.log(`[FeedbackReward] Refunded ${refundAmount} tokens to ${user.username || 'user'}`);
        }
        
        // Record feedback
        const feedbackRecord: FeedbackRecord = {
            messageId,
            sessionId,
            userId: user?.id || 'guest',
            feedbackType,
            timestamp: Date.now(),
            rewarded: true,
            rewardAmount: refundAmount,
            messageContent: previousUserMessage?.content || '',
            responseContent: message.content.substring(0, 500), // First 500 chars
            tokensCharged,
            complexity: this.detectComplexity(previousUserMessage?.content || ''),
            model: message.modelUsed || 'unknown',
            trainingData: {
                userQuery: previousUserMessage?.content || '',
                aiResponse: message.content,
                feedbackScore: feedbackType === 'up' ? 1 : -1,
                responseQuality: feedbackType === 'up' ? 'good' : 'bad',
                timestamp: Date.now(),
                metadata: {
                    tokensCharged,
                    messageLength: message.content.length,
                    hasAttachments: message.attachments?.length || 0,
                    model: message.modelUsed
                }
            }
        };
        
        this.feedbackHistory.push(feedbackRecord);
        this.rewardedMessages.add(messageId);
        this.updateCounts(feedbackType);
        this.saveState();
        this.saveTrainingData(feedbackRecord);
        
        return {
            success: true,
            rewarded: true,
            refundAmount,
            reason: `Thank you for your feedback! ${refundPercent}% refund applied.`,
            isFirstFeedback: true,
            abuseDetected: abuseCheck.action === 'warn',
            feedbackId
        };
    }
    
    /**
     * Calculate refund percentage based on AI response length (inverse scaling)
     * 
     * Logic: Shorter responses = higher refund % because less value was delivered
     *        Longer responses = lower refund % because substantial value was provided
     * 
     * This matches how token consumption works:
     * - Long responses consume more tokens, so lower % still gives reasonable refund
     * - Short responses consumed fewer tokens, so higher % compensates for feedback time
     */
    private calculateRefundPercent(
        message: Message,
        feedbackType: 'up' | 'down',
        previousMessage: Message | null
    ): number {
        const responseLength = message.content?.length || 0;
        const tiers = FEEDBACK_CONFIG.responseLengthTiers;
        const refunds = FEEDBACK_CONFIG.refundByLengthTier;
        
        // Determine base refund percentage based on response length tier
        let basePercent: number;
        
        if (responseLength < tiers.tiny) {
            basePercent = refunds.tiny;        // 20% for tiny responses
        } else if (responseLength < tiers.short) {
            basePercent = refunds.short;       // 15% for short
        } else if (responseLength < tiers.medium) {
            basePercent = refunds.medium;      // 12% for medium
        } else if (responseLength < tiers.long) {
            basePercent = refunds.long;        // 8% for long
        } else if (responseLength < tiers.veryLong) {
            basePercent = refunds.veryLong;    // 5% for very long
        } else {
            basePercent = refunds.massive;     // 3% for massive responses
        }
        
        console.log(`[FeedbackReward] Response length: ${responseLength} chars -> Base refund: ${basePercent}%`);
        
        // Apply bonuses
        let finalPercent = basePercent;
        
        // Bonus for thumbs down (more valuable feedback for improvement)
        if (feedbackType === 'down') {
            finalPercent += FEEDBACK_CONFIG.bonusForThumbsDown;
        }
        
        // Bonus for complex queries (user invested more thought)
        if (previousMessage) {
            const complexity = this.detectComplexity(previousMessage.content);
            if (complexity === 'complex' || complexity === 'advanced') {
                finalPercent += FEEDBACK_CONFIG.bonusForComplex;
            }
        }
        
        // Clamp to min/max bounds
        finalPercent = Math.min(
            FEEDBACK_CONFIG.maxRefundPercent, 
            Math.max(FEEDBACK_CONFIG.minRefundPercent, finalPercent)
        );
        
        console.log(`[FeedbackReward] Final refund: ${finalPercent}% (base: ${basePercent}%, thumbsDown: ${feedbackType === 'down'})`);
        
        return finalPercent;
    }
    
    /**
     * Detect complexity of the message
     */
    private detectComplexity(content: string): string {
        const lower = content.toLowerCase();
        let score = 0;
        
        // Complex indicators
        const complexPatterns = [
            /compute|calculate|solve|formula|equation/i,
            /explain.*detail|comprehensive|thorough/i,
            /compare|contrast|difference/i,
            /step.?by.?step|show.*work/i,
            /pfrs|pas|psa|nirc|audit/i,
            /journal.*entry|ledger|balance.*sheet/i
        ];
        
        complexPatterns.forEach(pattern => {
            if (pattern.test(lower)) score++;
        });
        
        if (content.length > 500) score++;
        if (content.length > 1000) score++;
        
        if (score >= 4) return 'advanced';
        if (score >= 2) return 'complex';
        if (score >= 1) return 'moderate';
        return 'simple';
    }
    
    /**
     * Detect potential abuse patterns
     */
    private detectAbuse(feedbackType: 'up' | 'down'): AbuseDetectionResult {
        const now = Date.now();
        
        // Reset hourly/daily counts if needed
        this.checkAndResetCounts();
        
        // Check time between feedbacks
        if (now - this.lastFeedbackTime < FEEDBACK_CONFIG.minTimeBetweenFeedbacks) {
            return {
                isAbuse: true,
                reason: 'Please wait a moment before providing more feedback',
                severity: 'low',
                action: 'warn'
            };
        }
        
        // Check hourly limit
        if (this.hourlyFeedbackCount >= FEEDBACK_CONFIG.maxFeedbacksPerHour) {
            return {
                isAbuse: true,
                reason: 'Hourly feedback limit reached. Try again later.',
                severity: 'medium',
                action: 'block'
            };
        }
        
        // Check daily limit
        if (this.dailyFeedbackCount >= FEEDBACK_CONFIG.maxFeedbacksPerDay) {
            return {
                isAbuse: true,
                reason: 'Daily feedback limit reached. Try again tomorrow.',
                severity: 'high',
                action: 'block'
            };
        }
        
        // Check for suspicious patterns (many consecutive same feedbacks)
        if (this.lastFeedbackType === feedbackType) {
            this.consecutiveSameFeedback++;
            if (this.consecutiveSameFeedback >= FEEDBACK_CONFIG.suspiciousPatternThreshold) {
                return {
                    isAbuse: true,
                    reason: 'Unusual feedback pattern detected',
                    severity: 'medium',
                    action: 'warn'
                };
            }
        } else {
            this.consecutiveSameFeedback = 1;
        }
        
        return {
            isAbuse: false,
            reason: '',
            severity: 'low',
            action: 'allow'
        };
    }
    
    /**
     * Update feedback counts
     */
    private updateCounts(feedbackType: 'up' | 'down'): void {
        this.hourlyFeedbackCount++;
        this.dailyFeedbackCount++;
        this.lastFeedbackTime = Date.now();
        this.lastFeedbackType = feedbackType;
    }
    
    /**
     * Check and reset counts if time periods have elapsed
     */
    private checkAndResetCounts(): void {
        const now = Date.now();
        
        if (now - this.hourlyResetTime >= FEEDBACK_CONFIG.hourlyResetInterval) {
            this.hourlyFeedbackCount = 0;
            this.hourlyResetTime = now;
        }
        
        if (now - this.dailyResetTime >= FEEDBACK_CONFIG.dailyResetInterval) {
            this.dailyFeedbackCount = 0;
            this.dailyResetTime = now;
            // Also reset consecutive counter daily
            this.consecutiveSameFeedback = 0;
        }
    }
    
    /**
     * Check if message has been rewarded
     */
    hasBeenRewarded(messageId: string): boolean {
        return this.rewardedMessages.has(messageId);
    }
    
    /**
     * Get feedback stats
     */
    getStats(): FeedbackStats {
        const thumbsUp = this.feedbackHistory.filter(f => f.feedbackType === 'up').length;
        const thumbsDown = this.feedbackHistory.filter(f => f.feedbackType === 'down').length;
        const totalRefunded = this.feedbackHistory.reduce((sum, f) => sum + f.rewardAmount, 0);
        
        return {
            totalFeedbacks: this.feedbackHistory.length,
            thumbsUp,
            thumbsDown,
            totalRefunded,
            uniqueMessagesRated: this.rewardedMessages.size,
            lastFeedbackTime: this.lastFeedbackTime
        };
    }
    
    /**
     * Get AI training data for export
     */
    getTrainingData(): FeedbackRecord['trainingData'][] {
        return this.feedbackHistory
            .filter(f => f.trainingData)
            .map(f => f.trainingData!);
    }
    
    /**
     * Save training data separately for AI improvement
     */
    private saveTrainingData(record: FeedbackRecord): void {
        try {
            const existing = localStorage.getItem(FEEDBACK_CONFIG.trainingDataKey);
            const trainingData: FeedbackRecord['trainingData'][] = existing ? JSON.parse(existing) : [];
            
            if (record.trainingData) {
                trainingData.push(record.trainingData);
                
                // Keep only last 100 training records (reduced from 500 to save space)
                if (trainingData.length > 100) {
                    trainingData.splice(0, trainingData.length - 100);
                }
                
                localStorage.setItem(FEEDBACK_CONFIG.trainingDataKey, JSON.stringify(trainingData));
            }
        } catch (error) {
            console.error('[FeedbackReward] Failed to save training data:', error);
            // If quota exceeded, just skip saving training data (non-critical)
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                // Clear training data entirely - it's not critical
                localStorage.removeItem(FEEDBACK_CONFIG.trainingDataKey);
                console.warn('[FeedbackReward] Cleared training data due to quota exceeded');
            }
        }
    }
    
    /**
     * Save state to localStorage with quota handling
     */
    private saveState(): void {
        try {
            const state = {
                rewardedMessages: Array.from(this.rewardedMessages).slice(-50), // Keep only last 50
                feedbackHistory: this.feedbackHistory.slice(-50), // Keep last 50 (reduced from 100)
                hourlyFeedbackCount: this.hourlyFeedbackCount,
                dailyFeedbackCount: this.dailyFeedbackCount,
                lastFeedbackTime: this.lastFeedbackTime,
                consecutiveSameFeedback: this.consecutiveSameFeedback,
                lastFeedbackType: this.lastFeedbackType,
                hourlyResetTime: this.hourlyResetTime,
                dailyResetTime: this.dailyResetTime
            };
            localStorage.setItem(FEEDBACK_CONFIG.storageKey, JSON.stringify(state));
        } catch (error) {
            console.error('[FeedbackReward] Failed to save state:', error);
            // If quota exceeded, try to clear old data and save minimal state
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                this.handleQuotaExceeded();
            }
        }
    }
    
    /**
     * Handle localStorage quota exceeded by clearing old data
     */
    private handleQuotaExceeded(): void {
        console.warn('[FeedbackReward] Quota exceeded, clearing old data...');
        try {
            // Clear training data (largest)
            localStorage.removeItem(FEEDBACK_CONFIG.trainingDataKey);
            
            // Keep only essential state
            this.feedbackHistory = this.feedbackHistory.slice(-10);
            const rewardedArray = Array.from(this.rewardedMessages);
            this.rewardedMessages = new Set(rewardedArray.slice(-20));
            
            // Try saving minimal state
            const minimalState = {
                rewardedMessages: Array.from(this.rewardedMessages),
                feedbackHistory: this.feedbackHistory,
                hourlyFeedbackCount: this.hourlyFeedbackCount,
                dailyFeedbackCount: this.dailyFeedbackCount,
                lastFeedbackTime: this.lastFeedbackTime
            };
            localStorage.setItem(FEEDBACK_CONFIG.storageKey, JSON.stringify(minimalState));
            console.log('[FeedbackReward] Saved minimal state after quota cleanup');
        } catch (e) {
            console.error('[FeedbackReward] Failed to save even minimal state:', e);
            // Last resort: just clear our storage completely
            localStorage.removeItem(FEEDBACK_CONFIG.storageKey);
            localStorage.removeItem(FEEDBACK_CONFIG.trainingDataKey);
        }
    }
    
    /**
     * Load state from localStorage
     */
    private loadState(): void {
        try {
            const stored = localStorage.getItem(FEEDBACK_CONFIG.storageKey);
            if (stored) {
                const state = JSON.parse(stored);
                this.rewardedMessages = new Set(state.rewardedMessages || []);
                this.feedbackHistory = state.feedbackHistory || [];
                this.hourlyFeedbackCount = state.hourlyFeedbackCount || 0;
                this.dailyFeedbackCount = state.dailyFeedbackCount || 0;
                this.lastFeedbackTime = state.lastFeedbackTime || 0;
                this.consecutiveSameFeedback = state.consecutiveSameFeedback || 0;
                this.lastFeedbackType = state.lastFeedbackType || null;
                this.hourlyResetTime = state.hourlyResetTime || Date.now();
                this.dailyResetTime = state.dailyResetTime || Date.now();
                
                // Check and reset counts on load
                this.checkAndResetCounts();
            }
        } catch (error) {
            console.error('[FeedbackReward] Failed to load state:', error);
        }
    }
    
    /**
     * Start periodic reset timer
     */
    private startPeriodicReset(): void {
        // Check every minute
        setInterval(() => {
            this.checkAndResetCounts();
        }, 60000);
    }
    
    /**
     * Clear all feedback data (for testing/admin)
     */
    clearAllData(): void {
        this.rewardedMessages.clear();
        this.feedbackHistory = [];
        this.hourlyFeedbackCount = 0;
        this.dailyFeedbackCount = 0;
        this.consecutiveSameFeedback = 0;
        this.lastFeedbackType = null;
        localStorage.removeItem(FEEDBACK_CONFIG.storageKey);
        localStorage.removeItem(FEEDBACK_CONFIG.trainingDataKey);
    }
}

// Singleton instance
export const feedbackRewardService = new FeedbackRewardService();

// Export convenience functions
export const processFeedbackReward = (
    message: Message,
    previousUserMessage: Message | null,
    feedbackType: 'up' | 'down',
    user: User | null,
    sessionId: string,
    updateUser?: (updates: Partial<User>) => void
) => feedbackRewardService.processFeedback(message, previousUserMessage, feedbackType, user, sessionId, updateUser);

export const hasMessageBeenRewarded = (messageId: string) => feedbackRewardService.hasBeenRewarded(messageId);
export const getFeedbackStats = () => feedbackRewardService.getStats();
export const getAITrainingData = () => feedbackRewardService.getTrainingData();

export default feedbackRewardService;

